'use client';

import { Component, type ReactNode } from 'react';

/**
 * Per-stack error boundary for the shared mobile chrome rows
 * (`MobileChromeTop` / `MobileChromeBottom` in `app-shell.tsx`).
 *
 * Why this exists, given the new root `global-error.tsx`:
 *
 * The shared mobile chrome wraps the global activity ticker, the
 * "My boards" rail, and the bottom nav — each of which renders
 * wallet-scoped data hooks (`useLobbySeatHoldings`,
 * `useLobbyHeaderStats`, `useGlobalActivity`). All three live
 * *outside* the lobby route group's `error.tsx`, so a synchronous
 * render-time throw from any of them used to escape every
 * boundary in the tree and surface as Next.js's bare
 *   "Application error: a client-side exception has occurred"
 * fallback. `global-error.tsx` now catches that worst case, but
 * "the entire app is replaced with an error screen" is a
 * heavy-handed response to e.g. a transient bad indexer payload
 * driving the rail. This boundary scopes the blast radius: a
 * broken chrome strip fails-soft to `null` and the route content
 * (which has its own `error.tsx`) keeps working, so the user can
 * still play / chat / connect even if the rail or ticker is
 * temporarily torn.
 *
 * The `label` prop drives the dev-only console diagnostic so a
 * later regression is obvious in DevTools without a full stack
 * trace dive — "[chrome-boundary][top] crashed" / "...[bottom]
 * crashed". Production logs the same payload (digest-less because
 * React error boundaries don't carry the Next.js digest) so a
 * support ticket lands enough context to triage.
 *
 * Resetting on remount is intentional: the chrome strips are
 * driven by hooks that re-fetch on a stale-time tick, so the
 * boundary self-clears as soon as the route re-mounts the strip
 * with fresh data. No imperative reset button — the
 * route-content error boundary remains the surface that asks the
 * user to retry.
 */
interface Props {
  /** Short label surfaced in dev-time console diagnostics. */
  label: 'top' | 'bottom';
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class MobileChromeErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // Always log — the boundary is the only signal that a chrome
    // hook crashed, and silently swallowing it would mask
    // recurring failures (e.g. a bad indexer field that lasts for
    // hours on a single wallet). Keep the payload structured so
    // the next reproduction is easy to spot in DevTools.
    console.error(`[chrome-boundary][${this.props.label}] crashed`, {
      label: this.props.label,
      name: error?.name,
      message: error?.message,
      componentStack: info.componentStack,
      error,
    });
  }

  render() {
    // Fail-soft: render nothing so the chrome row collapses out
    // of the flex column. `<main>` (`flex-1 min-h-0`) absorbs the
    // freed band automatically — see the `MobileChromeTop`
    // docstring in `app-shell.tsx` for the layout invariant.
    return this.state.hasError ? null : this.props.children;
  }
}
